// @flow
import Spinner from 'components/Spinner'
import { useLoadFormsAndDocuments } from 'queries/forms/useLoadForms'
import * as React from 'react'
import { toast } from 'react-toastify'
import type { Customer } from 'types/customer'
import type { Message } from 'types/message'
import type { TeamDetails } from 'types/team_details'
import { ZentakeContext } from 'utils/ZentakeContext'

import zentakeApi from '@/api'
import { DATE_FORMATS, getFormattedDate } from '@/utils/dates/getFormattedDate'

import useSelectPagination from './hooks/useSelectPagination'
import SendResourceModal from './SendResourceModal'

type Props = {
  onHide: () => void,
}

async function sendForm({
  personToUse,
  is_waiting,
  email,
  id,
  messages,
  setMessages,
  teamDetails,
  phone,
  willUseEmail,
  selectedOption,
  scheduledDate,
  reloadMessages,
}: {
  personToUse: Customer,
  is_waiting: boolean,
  email: string,
  id: string,
  messages: Array<Message>,
  teamDetails: ?TeamDetails,
  phone: string,
  willUseEmail: boolean,
  selectedOption: { label: string, value: string, type: string },
  scheduledDate: ?string,
  reloadMessages: () => void,
}) {
  try {
    const selectedResourceType = selectedOption.type

    const formId = selectedResourceType === 'form' ? id : null
    const documentId = selectedResourceType === 'pdf' ? id : null

    const newMessage = await zentakeApi.sendMessageToCustomer({
      targetEmail: email,
      customer: personToUse?.id,
      preferredDeliveryMethod: willUseEmail ? 'email' : 'sms',
      deliveryMethodUsed: willUseEmail ? 'email' : 'sms',
      messageType: 'form_response_request',
      formId,
      packetId: null,
      is_waiting,
      team: teamDetails?.uid,
      phoneNumber: phone,
      documentId,
      scheduledDate: getFormattedDate(scheduledDate, {
        format: DATE_FORMATS.ISO_DATETIME,
        utc: true,
      }),
    })
    reloadMessages?.()
    !is_waiting && toast.info('Form Sent')
    return newMessage
  } catch (error) {
    toast.error('Form not sent, please try again')
    throw error
  }
}

function SendForm({ onHide }: Props): React.Node {
  const { userDetails } = React.useContext(ZentakeContext)
  const [hasMore, setHasMore] = React.useState(false)

  const { params, onInputChange, onMenuScrollToBottom, setOptions, options } =
    useSelectPagination(hasMore)

  const { data, isLoading, isFetching, refetch } = useLoadFormsAndDocuments(
    { ...params },
    {
      staleTime: Infinity,
      keepPreviousData: true,
    }
  )

  React.useEffect(() => {
    setHasMore(Boolean(data?.next))
  }, [data?.next])

  const buildOptions = React.useCallback(
    (data) => {
      const newOptions = data?.results?.length
        ? data?.results
            .filter((form) => form.enabled && Boolean(form.name))
            .map((o) => ({
              value: o.id,
              label: o.name,
              hasOfficeUseQuestions: o.has_office_use_questions || false,
              type: o.type, // form or pdf
            }))
            .sort((a, b) => a.label?.localeCompare(b.label))
        : []

      setOptions((options) => [...options, ...newOptions])
    },
    [setOptions]
  )

  React.useEffect(() => {
    if (data?.results) {
      buildOptions(data)
    }
  }, [buildOptions, data])

  const dropdownLabel = (
    <div className="flex items-center">
      <span className="flex-1">Select Form</span>
      {isLoading && <Spinner color="onLight" size={20} />}
    </div>
  )

  const dropdownPlaceholder = data?.results?.length
    ? 'Select Form'
    : 'No Form Available'

  async function afterSelect() {
    onInputChange('')
    const { data } = await refetch()
    buildOptions(data)
  }

  return (
    <SendResourceModal
      showScheduleSendButton
      afterSelect={afterSelect}
      dropdownDisabled={isLoading}
      dropdownLabel={dropdownLabel}
      dropdownPlaceholder={dropdownPlaceholder}
      heading="Send Form"
      isFetching={isFetching}
      isVerified={userDetails?.is_verified || false}
      loading={isLoading}
      onHide={onHide}
      onMenuScrollToBottom={onMenuScrollToBottom}
      onType={onInputChange}
      options={options}
      sendResource={sendForm}
      subheading="Complete the following to send a form."
    />
  )
}

export default SendForm
